import React from 'react';
import { useNavigate } from 'react-router-dom';
import './SmartStopLoss.css';

const VcpGuide: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="article-page">
      {/* Back Navigation */}
      <div className="article-nav">
        <button className="back-button" onClick={() => navigate('/learning-hub')}>
          ← Back to Learning Hub
        </button>
      </div>

      {/* Article Header */}
      <header className="article-header">
        <div className="article-badge">ADVANCED PATTERN</div>
        <h1 className="article-title">How to Trade VCP (Volatility Contraction Pattern) Screener</h1>
        <div className="article-meta">
          <span className="meta-item">📅 Updated December 2024</span>
          <span className="meta-item">⏱️ 12 min read</span>
          <span className="meta-item">🎯 High Probability Setup</span>
        </div>
      </header>

      {/* Article Content */}
      <article className="article-content">

        {/* Introduction */}
        <section className="content-section">
          <h2>What is VCP Pattern?</h2>
          <div className="ai-badge-container">
            <span className="ai-badge">📊 Pattern Detected by Screener</span>
            <span className="market-badge">⭐ Mark Minervini's Strategy</span>
          </div>
          <p className="lead-text">
            The Volatility Contraction Pattern (VCP) is a powerful technical analysis pattern developed by legendary
            trader Mark Minervini to identify stocks with high bullish potential and minimal risk. This is one of the
            most profitable chart patterns in swing trading.
          </p>

          <div className="highlight-box">
            <strong>Mark Minervini's Track Record:</strong>
            <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
              <li>Won 1997 US Investing Championship</li>
              <li>Won again in 2021 with <strong>334.8% gain</strong> (broke all records)</li>
              <li>Achieved <strong>33,500% returns in just 5 years</strong> using primarily VCP strategy</li>
              <li>All returns are audited and verified</li>
            </ul>
          </div>

          <p>
            The VCP forms when both <strong>volatility and volume gradually decrease</strong> during a stock's consolidation,
            creating what Minervini calls the "line of least resistance." Each pullback in price gets progressively smaller,
            forming a tighter range as sellers lose interest. This contraction indicates that supply is drying up while
            demand is building, setting the stage for a powerful breakout.
          </p>

          {/* Image Placeholder */}
          <div className="animation-container">
            <div className="video-placeholder">
              <div className="placeholder-icon">📊</div>
              <p className="placeholder-text">VCP Pattern Illustration</p>
              <p className="placeholder-desc">Shows progressive tightening: 20% → 12% → 6% pullbacks forming base</p>
            </div>
          </div>
        </section>

        {/* How to Identify */}
        <section className="content-section">
          <h2>How to Identify VCP Pattern - Essential Criteria</h2>

          <h3>1. Long-Term Uptrend (Prerequisite)</h3>
          <div className="method-card">
            <p>The stock MUST be in a strong, stable upward trend before the VCP forms:</p>
            <ul>
              <li>Price trading above key moving averages (50-day, 150-day, and 200-day SMAs)</li>
              <li>Generally the result of institutional buying</li>
              <li>VCPs don't work in downtrends or weak markets</li>
            </ul>
          </div>

          <h3>2. Progressive Price Tightening (Critical!)</h3>
          <div className="method-card">
            <p>Each pullback must be <strong>smaller</strong> than the previous one:</p>
            <ul>
              <li><strong>Example progression:</strong> 15% → 10% → 5%</li>
              <li>Creates a tighter price range over time</li>
              <li>Forms from left to right, getting progressively tighter</li>
              <li>This is the MOST important characteristic</li>
            </ul>

            <div className="example-box">
              <h4>Real Example:</h4>
              <p><strong>First pullback:</strong> Stock drops 20% from high</p>
              <p><strong>Second pullback:</strong> Stock drops only 10% from next high</p>
              <p><strong>Third pullback:</strong> Stock drops only 5% from final high</p>
              <p className="result">✅ Perfect progressive tightening!</p>
            </div>
          </div>

          <h3>3. Number of Contractions</h3>
          <div className="method-card">
            <ul>
              <li><strong>Ideal:</strong> 3-4 clear contractions</li>
              <li><strong>Minimum:</strong> 2 contractions required</li>
              <li><strong>Maximum:</strong> More than 6 = pattern losing integrity</li>
              <li>Each contraction must be measurably smaller than the last</li>
            </ul>
          </div>

          <h3>4. Declining Volume and Volatility</h3>
          <div className="method-card">
            <ul>
              <li>Trading volume <strong>decreases</strong> during each pullback</li>
              <li>Signals reduced selling pressure</li>
              <li>Volume should be at its lowest during the final contraction</li>
              <li><strong>Volume spike (30-40% above average) required on breakout</strong></li>
            </ul>
          </div>

          <h3>5. Mark Minervini's Trend Template</h3>
          <div className="method-card">
            <ul>
              <li><strong>Relative Strength (RS) above 70:</strong> Stock outperforming at least 70% of the market</li>
              <li>Price above 50-day, 150-day, and 200-day SMAs</li>
              <li>Confirms stock is trading above key trend levels</li>
            </ul>
          </div>

          <h3>6. Base Formation Timeframe</h3>
          <div className="method-card">
            <ul>
              <li>Typically takes <strong>6-12 weeks</strong> to form</li>
              <li>This timeframe indicates institutional accumulation</li>
              <li>Too short = insufficient consolidation</li>
              <li>Too long = pattern may be losing momentum</li>
            </ul>
          </div>

          {/* Image Placeholder */}
          <div className="animation-container">
            <div className="video-placeholder">
              <div className="placeholder-icon">📈</div>
              <p className="placeholder-text">VCP Identification Checklist</p>
              <p className="placeholder-desc">Annotated chart showing all 6 criteria on actual stock example</p>
            </div>
          </div>
        </section>

        {/* Entry Rules */}
        <section className="content-section">
          <h2>VCP Entry Rules</h2>

          <div className="trailing-rules">
            <div className="rule-card">
              <div className="rule-number">Entry #1</div>
              <h4>Breakout Entry (Most Common)</h4>
              <p>Enter when price breaks above the pivot point (highest resistance of consolidation).</p>
              <ul>
                <li>MUST be accompanied by <strong>high volume</strong> (30-40% above average minimum)</li>
                <li>Breakout confirms strong demand has taken over</li>
                <li>Best for aggressive traders</li>
                <li>Enter immediately after breakout candle closes</li>
              </ul>

              <div className="example-calc">
                <p><strong>Pivot Point:</strong> ₹500 (resistance during final contraction)</p>
                <p><strong>Breakout:</strong> Price closes at ₹505 with 2x normal volume</p>
                <p><strong>Entry:</strong> Buy at ₹505 (or slightly above)</p>
                <p className="result">✅ Breakout confirmed with volume!</p>
              </div>
            </div>

            <div className="rule-card">
              <div className="rule-number">Entry #2</div>
              <h4>Retest Entry (Conservative)</h4>
              <p>Wait for a small pullback after the initial breakout to get a better entry price.</p>
              <ul>
                <li>Enter when price retests the breakout level and holds</li>
                <li>Provides better risk/reward for conservative traders</li>
                <li>Confirms breakout level has become support</li>
                <li>Not all breakouts will pull back</li>
              </ul>

              <div className="example-calc">
                <p><strong>Breakout:</strong> ₹505</p>
                <p><strong>Pullback:</strong> Price dips to ₹502 next day</p>
                <p><strong>Entry:</strong> Buy at ₹502 when it bounces off ₹500 support</p>
                <p className="result">✅ Better entry, tighter stop-loss!</p>
              </div>
            </div>
          </div>

          <div className="warning-box">
            <strong>⚠️ Volume Confirmation is CRITICAL:</strong> Without a 30-40% volume spike on the breakout,
            the pattern has a much higher failure rate. Never enter VCP breakouts on low volume!
          </div>

          {/* Image Placeholder */}
          <div className="animation-container">
            <div className="video-placeholder">
              <div className="placeholder-icon">🎯</div>
              <p className="placeholder-text">VCP Breakout Entry Animation</p>
              <p className="placeholder-desc">Shows price breaking pivot with volume surge and entry points</p>
            </div>
          </div>
        </section>

        {/* Profit Targets */}
        <section className="content-section">
          <h2>Profit Targets and Exit Strategy</h2>

          <h3>Method 1: Measured Move</h3>
          <div className="method-card">
            <p>Measure the height of the entire base pattern and project that distance from the breakout point</p>
            <div className="example-box">
              <h4>Example:</h4>
              <p><strong>Base Low:</strong> ₹400</p>
              <p><strong>Breakout Point:</strong> ₹500 (base height = ₹100)</p>
              <p><strong>Target:</strong> ₹500 + ₹100 = ₹600</p>
              <p className="result">✅ 20% upside target from breakout</p>
            </div>
          </div>

          <h3>Method 2: Percentage Targets</h3>
          <div className="method-card">
            <p>Set percentage-based targets from breakout point</p>
            <div className="example-box">
              <h4>Example:</h4>
              <p><strong>Entry:</strong> ₹505</p>
              <p><strong>Conservative Target:</strong> ₹555 (10% gain)</p>
              <p><strong>Standard Target:</strong> ₹630 (25% gain)</p>
              <p><strong>Extended Target:</strong> ₹757 (50% gain)</p>
            </div>
          </div>

          <h3>Professional Exit Strategy: Scaling Out</h3>
          <div className="method-card">
            <ol>
              <li><strong>Sell 1/3 of position at conservative target</strong> (10-15%) - Locks in some profit</li>
              <li><strong>Sell 1/3 at standard target</strong> (25-30%) - Reduces exposure further</li>
              <li><strong>Let final 1/3 run</strong> - Captures big moves if they develop</li>
            </ol>
            <p style={{ marginTop: '16px' }}>This approach balances profit-taking with staying in for larger moves.</p>
          </div>

          <div className="highlight-box">
            <strong>Realistic Profit Expectations:</strong>
            <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
              <li><strong>Successful VCP breakouts:</strong> Often see gains of 20-100% over following months</li>
              <li><strong>Short-term swing trades:</strong> Target 15-30% gains</li>
              <li><strong>60-70% of VCP breakouts</strong> lead to solid rallies when breaking with strong volume</li>
            </ul>
          </div>
        </section>

        {/* Common Mistakes */}
        <section className="content-section">
          <h2>Common Mistakes to Avoid</h2>

          <div className="mistake-card">
            <h4>❌ Mistake #1: Entering Too Early</h4>
            <p>
              Jumping in before confirmed breakout gets you caught in false moves or deeper contractions.
              <strong> Always wait for price to break pivot with volume confirmation.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #2: Ignoring Volume Confirmation</h4>
            <p>
              Entering on breakouts without volume surge leads to high failure rate.
              <strong> Always require 30-40% volume spike minimum.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #3: Insufficient Contractions</h4>
            <p>
              Entering after only 1 pullback means pattern not fully developed.
              <strong> Require minimum 2-3 clear contractions.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #4: Ignoring the Broader Trend</h4>
            <p>
              Trading VCPs in downtrends or weak markets leads to failures even with good patterns.
              <strong> Only trade VCPs during bull markets or strong uptrends.</strong>
            </p>
          </div>

          <div className="mistake-card">
            <h4>❌ Mistake #5: Not Waiting for Progressive Tightening</h4>
            <p>
              Pattern lacks key characteristic if pullbacks aren't getting smaller (20% → 12% → 6% is good).
              <strong> Verify each contraction is tighter than the last.</strong>
            </p>
          </div>
        </section>

        {/* Real Examples */}
        <section className="content-section">
          <h2>Real Trading Examples</h2>

          <div className="method-card">
            <h4>Example 1: Tesla (TSLA)</h4>
            <ul>
              <li><strong>Setup:</strong> After 600% rally, formed 3-month VCP</li>
              <li><strong>Contractions:</strong> 25% → 20% → 10% (final)</li>
              <li><strong>Pattern:</strong> Clear progressive tightening</li>
              <li><strong>Breakout:</strong> Broke to new highs with volume</li>
              <li><strong>Result:</strong> Advanced another <strong>90%</strong> after breakout</li>
            </ul>
          </div>

          <div className="method-card">
            <h4>Example 2: Nvidia (NVDA) - 2025</h4>
            <ul>
              <li><strong>Context:</strong> After sharp rally on AI-driven earnings</li>
              <li><strong>Week 1-2:</strong> Price drops 15% with high volume</li>
              <li><strong>Week 3-4:</strong> Drops only 8% with lower volume</li>
              <li><strong>Week 5-6:</strong> Pullback contracts to 4%, volume hits 2-month low</li>
              <li><strong>Breakout:</strong> Week 7 on earnings beat with 3x normal volume</li>
              <li><strong>Result:</strong> <strong>30% rally in just 10 trading days</strong></li>
            </ul>
          </div>

          <div className="highlight-box">
            <strong>Success Rate Statistics:</strong>
            <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
              <li><strong>60-70%</strong> of VCP breakouts lead to solid rallies (with volume confirmation)</li>
              <li>Success rate highest in bull markets</li>
              <li>Typical risk/reward: <strong>3:1 or better</strong></li>
              <li>Small losses (5-8%), large gains (20-100%) = profitable over time</li>
            </ul>
          </div>
        </section>

        {/* Quick Reference */}
        <section className="content-section">
          <h2>VCP Trading Quick Reference</h2>

          <div className="reference-table">
            <table>
              <thead>
                <tr>
                  <th>Criteria</th>
                  <th>Requirement</th>
                  <th>Why It Matters</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Contractions</td>
                  <td>2-5 (ideal: 3-4)</td>
                  <td>Confirms pattern formation</td>
                </tr>
                <tr>
                  <td>Progressive Tightening</td>
                  <td>Each smaller than previous</td>
                  <td>Shows supply drying up</td>
                </tr>
                <tr>
                  <td>Volume on Breakout</td>
                  <td>30-40% above average minimum</td>
                  <td>Confirms institutional buying</td>
                </tr>
                <tr>
                  <td>Profit Target</td>
                  <td>15-50% from breakout</td>
                  <td>Scale out at different levels</td>
                </tr>
                <tr>
                  <td>Relative Strength</td>
                  <td>RS above 70</td>
                  <td>Outperforming market</td>
                </tr>
                <tr>
                  <td>Base Duration</td>
                  <td>6-12 weeks</td>
                  <td>Institutional accumulation</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Summary */}
        <section className="content-section summary-section">
          <h2>Key Takeaways</h2>
          <ul className="takeaway-list">
            <li>✅ VCP is a high-probability pattern with 60-70% success rate in bull markets</li>
            <li>✅ Requires progressive tightening (each pullback smaller: 20% → 12% → 6%)</li>
            <li>✅ MUST have volume confirmation (30-40% spike) on breakout - non-negotiable</li>
            <li>✅ Target 15-50% gains, scaling out at different levels</li>
            <li>✅ Only trade in uptrends with RS above 70</li>
            <li>✅ Wait for full pattern development (2-5 contractions, 6-12 weeks)</li>
            <li>✅ Mark Minervini achieved 33,500% in 5 years using this pattern (334.8% in 2021 alone)</li>
          </ul>

          <div className="final-note">
            <strong>Remember:</strong> The VCP provides one of the most reliable and profitable chart patterns
            in swing trading. Our VCP Screener automatically identifies stocks with progressive volatility contraction,
            filtering for proper volume and tightening patterns. Let our screener find these high-probability setups while you
            focus on execution. For stop-loss techniques, check our dedicated Smart Stop-Loss guide.
          </div>
        </section>

      </article>

      {/* Related Articles */}
      <section className="related-articles">
        <h3>Continue Learning</h3>
        <div className="related-grid">
          <div className="related-card" onClick={() => navigate('/learning-hub/smart-stoploss')}>
            <div className="related-icon">🛡️</div>
            <h4>Smart Stop-Loss Techniques</h4>
            <p>Master AI-powered stop-loss strategies for profitable trading</p>
          </div>
          <div className="related-card" onClick={() => navigate('/learning-hub/flag-pennant-guide')}>
            <div className="related-icon">🏴</div>
            <h4>Flag & Pennant Patterns</h4>
            <p>Learn continuation patterns and breakout timing</p>
          </div>
          <div className="related-card" onClick={() => navigate('/learning-hub')}>
            <div className="related-icon">📚</div>
            <h4>View All Guides</h4>
            <p>Explore all swing trading tutorials</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="article-cta">
        <h3>Ready to Find VCP Setups?</h3>
        <p>Use our VCP screener to find stocks forming Volatility Contraction Patterns</p>
        <button className="cta-button" onClick={() => navigate('/screener/vcp')}>
          Try VCP Screener Now →
        </button>
      </section>

    </div>
  );
};

export default VcpGuide;
